'use client';
import { useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ToastProvider';
import { ROUTES } from '@/lib/config/routes.config';
import { clearAllAuthData } from '@/utils/auth.utils';
import { clearAuthCookies } from '@/app/actions/auth.actions';
import { logger } from '@/utils/logger';

/**
 * Session Monitor Hook
 * Handles automatic logout on session expiry (401 errors)
 * Note: Cannot monitor JWT expiration directly since token is in httpOnly cookie
 */
export function useSessionMonitor() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { showError } = useToast();
  const hasHandledExpiryRef = useRef(false);

  const handleSessionExpired = useCallback(async () => {
    if (hasHandledExpiryRef.current) {
      return;
    }

    hasHandledExpiryRef.current = true;

    logger.log('🔒 [Session Monitor] Session expired - logging out');

    // Show error to user
    // showError('Session Expired', 'Your session has expired. Please login again.', 5000);

    // Clear all auth data
    clearAllAuthData();
    await clearAuthCookies();
    queryClient.clear();

    // Redirect to login
    const currentPath = window.location.pathname;
    if (currentPath !== ROUTES.PUBLIC.LOGIN) {
      logger.log('→ Redirecting to login from:', currentPath);
      router.push(
        `${ROUTES.PUBLIC.LOGIN}?returnUrl=${encodeURIComponent(currentPath)}&session_expired=true`
      );
    }

    // Reset flag after delay
    setTimeout(() => {
      hasHandledExpiryRef.current = false;
    }, 2000);
  }, [router, queryClient, showError]);

  // Listen for 401 errors from API (dispatched by API client)
  useEffect(() => {
    const handleSessionExpiredEvent = () => {
      logger.log('🔔 [Session Monitor] Received session-expired event');
      handleSessionExpired();
    };

    window.addEventListener('session-expired' as any, handleSessionExpiredEvent);

    return () => {
      window.removeEventListener('session-expired' as any, handleSessionExpiredEvent);
    };
  }, [handleSessionExpired]);

  return { handleSessionExpired };
}
